import React from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { blogCategories as defaultCategories, blogArticles as defaultArticles } from "../../data/blogContent";
import { Link } from "react-router-dom";

const BlogHome1 = ({
  basePath = "/blog",
  categoriesMap = defaultCategories,
  articles = defaultArticles,
  badge = "Inspiración financiera",
  title = "MEU Blog",
  description = "Inspírate con ideas accionables para manejar tus finanzas, potenciar tu empresa, cuidar a tu equipo y leer el mercado con visión estratégica. Explora nuestras categorías y encuentra artículos profundos hechos por especialistas.",
  categoriesHeading = "Navega por las categorías del MEU Blog",
}) => {
  const categories = Object.values(categoriesMap);

  const getLatestArticleInCategory = (slug) =>
    articles
      .filter((article) => article.category === slug)
      .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))[0];

  const CategoryNextArrow = ({ onClick }) => (
    <button
      type="button"
      className="absolute top-1/2 -right-8 z-10 hidden xl:flex -translate-y-1/2 items-center justify-center h-12 w-12 rounded-full bg-white text-[#102a72] shadow-lg border border-white/60 hover:bg-[#1746a2] hover:text-white transition-all duration-200"
      onClick={onClick}
      aria-label="Siguiente categoría"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M9.29 6.71a1 1 0 011.42 0L15 11l-4.29 4.29a1 1 0 11-1.42-1.42L12.17 12l-2.88-2.88a1 1 0 010-1.41z" />
      </svg>
    </button>
  );

  const CategoryPrevArrow = ({ onClick }) => (
    <button
      type="button"
      className="absolute top-1/2 -left-8 z-10 hidden xl:flex -translate-y-1/2 items-center justify-center h-12 w-12 rounded-full bg-white text-[#102a72] shadow-lg border border-white/60 hover:bg-[#1746a2] hover:text-white transition-all duration-200"
      onClick={onClick}
      aria-label="Categoría anterior"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M14.71 6.71a1 1 0 010 1.41L11.83 12l2.88 2.88a1 1 0 01-1.42 1.42L9 12l4.29-4.29a1 1 0 011.42 0z" />
      </svg>
    </button>
  );

  const categorySettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    nextArrow: <CategoryNextArrow />,
    prevArrow: <CategoryPrevArrow />,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
        },
      },
    ],
  };

  return (
    <div className="w-full">
      <section className="bg-gradient-to-br from-[#0d1f58] via-[#102a72] to-[#1746a2] py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-12">
            <div className="lg:w-1/2 text-center lg:text-left">
              <span className="inline-flex items-center px-4 py-1 rounded-full bg-white/10 text-white/80 uppercase text-sm tracking-widest font-semibold mx-auto lg:mx-0">
                {badge}
              </span>
              <h1
                className="mt-6 text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight"
                style={{ textShadow: "0px 18px 36px rgba(6, 18, 55, 0.55)" }}
              >
                {title}
              </h1>
              <p className="mt-6 text-base sm:text-lg lg:text-xl text-white/80 leading-relaxed">
                {description}
              </p>
            </div>
            <div className="lg:w-1/2">
              <div className="rounded-3xl p-6 sm:p-8 border border-white/20 bg-white/10 backdrop-blur-md shadow-[0_20px_50px_rgba(9,20,71,0.35)]">
                <h2 className="text-2xl font-semibold text-white mb-4">
                  ¿Qué encontrarás aquí?
                </h2>
                <ul className="space-y-3 text-white/80 text-base leading-relaxed">
                  <li>• Guías prácticas con pasos accionables.</li>
                  <li>• Análisis de actualidad financiera en lenguaje claro.</li>
                  <li>• Historias inspiradoras y casos aplicados al contexto colombiano.</li>
                </ul>
                <p className="mt-6 text-sm text-white/60">
                  Actualizamos el blog cada mes con nuevas investigaciones y herramientas descargables.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#f4f9fb] py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-10">
            <div className="text-center sm:text-left">
              <p className="text-sm font-semibold text-[#1746a2] uppercase tracking-[0.3em]">
                Temas que exploramos
              </p>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#102a72] mt-3 leading-tight">
                {categoriesHeading}
              </h2>
            </div>
            <p className="text-gray-600 max-w-md text-sm sm:text-base text-center sm:text-left mx-auto sm:mx-0">
              Elige un tema y encuentra colecciones curadas de artículos,
              noticias y guías para avanzar a tu ritmo.
            </p>
          </div>
          <Slider
            {...categorySettings}
            className="blog-category-slider [&_.slick-track]:flex [&_.slick-track]:justify-center [&_.slick-slide]:px-3 sm:[&_.slick-slide]:px-6 [&_.slick-slide]:py-8 sm:[&_.slick-slide]:py-10 [&_.slick-slide]:h-auto [&_.slick-list]:overflow-visible sm:[&_.slick-list]:overflow-hidden px-2 sm:px-6"
          >
            {categories.map((category) => {
              const latest = getLatestArticleInCategory(category.slug);
              return (
                <div key={category.slug} className="h-full">
                  <article className="bg-white rounded-3xl shadow-md hover:shadow-[0_26px_46px_rgba(15,26,73,0.22)] hover:-translate-y-1.5 transition-all duration-300 border border-blue-100 flex flex-col h-full min-h-[460px] sm:min-h-[520px] max-w-[360px] w-full mx-auto">
                    <Link
                      to={`${basePath}/${category.slug}`}
                      className="block h-52 bg-cover bg-center rounded-t-3xl"
                      style={{ backgroundImage: `url(${category.cardImage})` }}
                      aria-label={`Explorar artículos de ${category.title}`}
                    />
                    <div className="p-6 flex flex-col gap-4 flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold text-[#123070]">
                          {category.title}
                        </h3>
                        <span className="text-xs uppercase tracking-wide text-blue-600 font-semibold">
                          {latest ? "Nuevo" : "Explorar"}
                        </span>
                      </div>
                      <p className="text-gray-600 leading-relaxed">{category.subtitle}</p>
                      {latest && (
                        <div className="mt-2 bg-blue-50 border border-blue-100 rounded-2xl p-4">
                          <p className="text-xs uppercase text-blue-600 font-semibold tracking-wide">
                            Artículo destacado
                          </p>
                          <h4 className="text-base font-semibold text-[#123070] leading-tight mt-1">
                            {latest.title}
                          </h4>
                          <Link
                            to={`${basePath}/${latest.category}/${latest.slug}`}
                            className="text-sm font-semibold text-blue-700 hover:text-blue-900 mt-2 inline-flex items-center gap-1"
                          >
                            Leer ahora
                            <span aria-hidden="true">→</span>
                          </Link>
                        </div>
                      )}
                      <div className="mt-auto">
                        <Link
                          to={`${basePath}/${category.slug}`}
                          className="inline-flex items-center gap-2 text-blue-700 font-semibold hover:text-blue-900 transition-colors duration-150"
                        >
                          Ver todos los artículos
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </article>
                </div>
              );
            })}
          </Slider>
        </div>
      </section>
    </div>
  );
};

export default BlogHome1;
